import {
  getRegistrantLicenseMapPinsOptions,
  getRegistrantLicensesOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import {
  exportRegistrantLicenses,
  getRegistrantLicenseFilterValues,
} from "@/hera-api/client/sdk.gen";
import type {
  NonRegistrantLicenseMapPinSchema,
  RegistrantLicenseListLicenseResponseSchema,
  RegistrantLicenseListQuestionColumnSchema,
} from "@/hera-api/client/types.gen";
import {
  BACKGROUND_QUERY_META,
  isForegroundFetch,
} from "@/lib/foreground-fetch";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import type { MapCluster, MapViewport } from "@/lib/map-clusters";
import { registrantLicensesQueryOptions } from "@/lib/registrant-dashboard-query-invalidation";
import { trackServerRequest } from "@/lib/server-request-activity";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import {
  type LicenseListSortDir,
  serializeLicenseListFilters,
} from "./use-paginated-non-registrant-licenses";
import { usePrefetchAdjacentListPages } from "./use-prefetch-adjacent-list-pages";

export type RegistrantLicenseListTab =
  | "all_active"
  | "needs_attention"
  | "compliance_issues"
  | "balance_due"
  | "renewable"
  | "inactive";

/** Stable empties — inline `?? []` would break useMemo/effect deps every render. */
const EMPTY_LICENSES: RegistrantLicenseListLicenseResponseSchema[] = [];
const EMPTY_QUESTION_COLUMNS: RegistrantLicenseListQuestionColumnSchema[] = [];
const EMPTY_STRINGS: string[] = [];
const EMPTY_PINS: NonRegistrantLicenseMapPinSchema[] = [];

export function usePaginatedRegistrantLicenses({
  jurisdictionDisplayCodes,
  licenseTypeIds,
  page,
  pageSize,
  sortBy,
  sortDir,
  columnFilters,
  tab,
  balanceDueCohort,
  bulkPaymentCohort,
  registrantAccountId,
  amsQuestionId,
  amsCompanyName,
  enabled,
}: {
  jurisdictionDisplayCodes: string[];
  licenseTypeIds?: string[];
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: LicenseListSortDir;
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  balanceDueCohort?: boolean;
  bulkPaymentCohort?: boolean;
  registrantAccountId?: string | null;
  amsQuestionId?: string | null;
  amsCompanyName?: string | null;
  enabled: boolean;
}) {
  const mergedFilters = useMemo(() => {
    const next: Record<string, string[]> = { ...columnFilters };
    if (jurisdictionDisplayCodes.length > 0) {
      next["jurisdiction.display_code"] = jurisdictionDisplayCodes;
    }
    return next;
  }, [columnFilters, jurisdictionDisplayCodes]);
  const filtersJson = serializeLicenseListFilters(mergedFilters);
  const query = useMemo(
    () => ({
      license_type_ids:
        licenseTypeIds && licenseTypeIds.length > 0
          ? licenseTypeIds
          : undefined,
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
      filters: filtersJson,
      tab,
      balance_due_cohort: balanceDueCohort || undefined,
      bulk_payment_cohort: bulkPaymentCohort || undefined,
      registrant_account_id: registrantAccountId || undefined,
      ams_question_id: amsQuestionId || undefined,
      ams_company_name: amsCompanyName || undefined,
    }),
    [
      licenseTypeIds,
      page,
      pageSize,
      sortBy,
      sortDir,
      filtersJson,
      tab,
      balanceDueCohort,
      bulkPaymentCohort,
      registrantAccountId,
      amsQuestionId,
      amsCompanyName,
    ],
  );

  const queryClient = useQueryClient();
  const result = useQuery({
    ...getRegistrantLicensesOptions({ query }),
    enabled,
    placeholderData: keepPreviousData,
    ...registrantLicensesQueryOptions,
  });

  const prefetchPage = useCallback(
    (adjacentPage: number) => {
      void queryClient.prefetchQuery({
        ...getRegistrantLicensesOptions({
          query: { ...query, page: adjacentPage },
        }),
        ...registrantLicensesQueryOptions,
        meta: BACKGROUND_QUERY_META,
      });
    },
    [query, queryClient],
  );

  usePrefetchAdjacentListPages({
    enabled,
    isFetching: result.isFetching,
    page,
    pageSize,
    totalCount: result.data?.total_count ?? 0,
    prefetchPage,
  });

  const licenses = result.data?.licenses ?? EMPTY_LICENSES;

  useEffect(() => {
    if (!enabled) return;
    setLicenseDisplayedSnapshot({
      pageLicenseIds: licenses.map((row) => row.id),
      pageJlts: licenses.map((row) => ({
        licenseId: row.id,
        jurisdictionId: row.jurisdiction.id,
        licenseTypeId: row.license_type.id,
        registrantAccountId: row.registrant_account_id ?? null,
      })),
      headerLicenseTypeIds: licenseTypeIds ?? EMPTY_STRINGS,
      headerRegistrantAccountIds: registrantAccountId
        ? [registrantAccountId]
        : EMPTY_STRINGS,
    });
  }, [enabled, licenses, licenseTypeIds, registrantAccountId]);

  return {
    licenses,
    totalCount: result.data?.total_count ?? 0,
    hasMultipleRegistrantAccounts:
      result.data?.has_multiple_registrant_accounts ?? false,
    questionColumns: result.data?.question_columns ?? EMPTY_QUESTION_COLUMNS,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
    error: result.error,
    dataUpdatedAt: result.dataUpdatedAt,
    refetch: result.refetch,
    listQuery: query,
  };
}

export function useRegistrantLicenseMapPins({
  jurisdictionDisplayCodes,
  licenseTypeIds,
  columnFilters,
  tab,
  balanceDueCohort,
  bulkPaymentCohort,
  registrantAccountId,
  amsQuestionId,
  amsCompanyName,
  enabled,
  viewport = null,
}: {
  jurisdictionDisplayCodes: string[];
  licenseTypeIds?: string[];
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  balanceDueCohort?: boolean;
  bulkPaymentCohort?: boolean;
  registrantAccountId?: string | null;
  amsQuestionId?: string | null;
  amsCompanyName?: string | null;
  enabled: boolean;
  viewport?: MapViewport | null;
}) {
  const queryClient = useQueryClient();
  const mergedFilters = useMemo(() => {
    const next: Record<string, string[]> = { ...columnFilters };
    if (jurisdictionDisplayCodes.length > 0) {
      next["jurisdiction.display_code"] = jurisdictionDisplayCodes;
    }
    return next;
  }, [columnFilters, jurisdictionDisplayCodes]);
  const filtersJson = serializeLicenseListFilters(mergedFilters);
  const query = useMemo(
    () => ({
      license_type_ids:
        licenseTypeIds && licenseTypeIds.length > 0
          ? licenseTypeIds
          : undefined,
      filters: filtersJson,
      tab,
      balance_due_cohort: balanceDueCohort || undefined,
      bulk_payment_cohort: bulkPaymentCohort || undefined,
      registrant_account_id: registrantAccountId || undefined,
      ams_question_id: amsQuestionId || undefined,
      ams_company_name: amsCompanyName || undefined,
      north: viewport?.north,
      south: viewport?.south,
      east: viewport?.east,
      west: viewport?.west,
      zoom: viewport?.zoom,
    }),
    [
      licenseTypeIds,
      filtersJson,
      tab,
      balanceDueCohort,
      bulkPaymentCohort,
      registrantAccountId,
      amsQuestionId,
      amsCompanyName,
      viewport,
    ],
  );

  const queryOptions = useMemo(
    () => getRegistrantLicenseMapPinsOptions({ query }),
    [query],
  );
  const result = useQuery({
    ...queryOptions,
    enabled,
    placeholderData: keepPreviousData,
  });
  const cancel = useCallback(
    () =>
      queryClient.cancelQueries({
        queryKey: queryOptions.queryKey,
        exact: true,
      }),
    [queryClient, queryOptions.queryKey],
  );

  const pins = result.data?.pins ?? EMPTY_PINS;
  const clusters = (result.data?.clusters ?? []) as MapCluster[];
  const mode = result.data?.mode ?? "pins";

  useEffect(() => {
    if (!enabled) {
      setLicenseDisplayedSnapshot({
        pinLicenseIds: EMPTY_STRINGS,
        pinJlts: [],
        mapIsClustered: false,
      });
      return;
    }
    setLicenseDisplayedSnapshot({
      pinLicenseIds: mode === "pins" ? pins.map((pin) => pin.id) : [],
      pinJlts:
        mode === "pins"
          ? pins.map((pin) => ({
              licenseId: pin.id,
              jurisdictionId: pin.jurisdiction_id,
              licenseTypeId: pin.license_type_id,
            }))
          : [],
      mapIsClustered: mode === "clusters",
    });
  }, [enabled, mode, pins]);

  return {
    pins,
    clusters,
    mode,
    totalInView: result.data?.total_in_view ?? pins.length,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
    error: result.error,
    refetch: result.refetch,
    cancel,
  };
}

export async function fetchRegistrantLicenseFilterValues(args: {
  column: string;
  jurisdictionDisplayCodes: string[];
  licenseTypeIds?: string[];
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  balanceDueCohort?: boolean;
  bulkPaymentCohort?: boolean;
  registrantAccountId?: string | null;
  amsQuestionId?: string | null;
  amsCompanyName?: string | null;
  search?: string;
}): Promise<string[]> {
  const merged: Record<string, string[]> = { ...args.columnFilters };
  if (args.jurisdictionDisplayCodes.length > 0) {
    merged["jurisdiction.display_code"] = args.jurisdictionDisplayCodes;
  }
  const q = args.search?.trim() || undefined;
  const { data } = await trackServerRequest(() =>
    getRegistrantLicenseFilterValues({
      query: {
        column: args.column,
        license_type_ids:
          args.licenseTypeIds && args.licenseTypeIds.length > 0
            ? args.licenseTypeIds
            : undefined,
        filters: serializeLicenseListFilters(merged),
        tab: args.tab,
        balance_due_cohort: args.balanceDueCohort || undefined,
        bulk_payment_cohort: args.bulkPaymentCohort || undefined,
        registrant_account_id: args.registrantAccountId || undefined,
        ams_question_id: args.amsQuestionId || undefined,
        ams_company_name: args.amsCompanyName || undefined,
        q,
      },
    }),
  );
  return data?.values ?? [];
}

export async function fetchRegistrantLicenseExport(args: {
  jurisdictionDisplayCodes: string[];
  licenseTypeIds?: string[];
  columnFilters: Record<string, string[]>;
  sortBy: string;
  sortDir: LicenseListSortDir;
  tab?: RegistrantLicenseListTab;
  balanceDueCohort?: boolean;
  bulkPaymentCohort?: boolean;
  registrantAccountId?: string | null;
  amsQuestionId?: string | null;
  amsCompanyName?: string | null;
}): Promise<RegistrantLicenseListLicenseResponseSchema[]> {
  const merged: Record<string, string[]> = { ...args.columnFilters };
  if (args.jurisdictionDisplayCodes.length > 0) {
    merged["jurisdiction.display_code"] = args.jurisdictionDisplayCodes;
  }
  const { data } = await trackServerRequest(() =>
    exportRegistrantLicenses({
      query: {
        license_type_ids:
          args.licenseTypeIds && args.licenseTypeIds.length > 0
            ? args.licenseTypeIds
            : undefined,
        filters: serializeLicenseListFilters(merged),
        sort_by: args.sortBy,
        sort_dir: args.sortDir,
        tab: args.tab,
        balance_due_cohort: args.balanceDueCohort || undefined,
        bulk_payment_cohort: args.bulkPaymentCohort || undefined,
        registrant_account_id: args.registrantAccountId || undefined,
        ams_question_id: args.amsQuestionId || undefined,
        ams_company_name: args.amsCompanyName || undefined,
      },
    }),
  );
  return (data?.licenses ?? []) as RegistrantLicenseListLicenseResponseSchema[];
}
